import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Wallet, Download, Clock, CheckCircle2, HandCoins, FileText, Award } from "lucide-react";
import { openAuthedPdf } from "@/components/StatementsList";
import ShareImpact from "@/components/ShareImpact";

export default function Earnings() {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(0);
  const thisYear = new Date().getFullYear();
  const [basis, setBasis] = useState("fy");
  const [year, setYear] = useState(new Date().getMonth() >= 3 ? thisYear : thisYear - 1);
  const [taxBusy, setTaxBusy] = useState(false);
  const [certBusy, setCertBusy] = useState(false);
  const certPdf = async () => {
    setCertBusy(true);
    try {
      const res = await fetch(`${API}/helpers/me/certificate.pdf?year=${year}&basis=${basis}`, { headers: { Authorization: `Bearer ${localStorage.getItem("kindred_token")}` } });
      if (!res.ok) { const j = await res.json().catch(() => ({})); toast.error(j.detail || "Couldn't prepare the certificate."); return; }
      const url = URL.createObjectURL(await res.blob());
      const w = window.open(url, "_blank");
      if (!w) { const a = document.createElement("a"); a.href = url; a.download = "kindred-certificate.pdf"; a.click(); }
    } catch { toast.error("Couldn't prepare the certificate."); }
    finally { setCertBusy(false); }
  };
  const years = [thisYear, thisYear - 1, thisYear - 2];
  const taxPdf = async () => {
    setTaxBusy(true);
    try { await openAuthedPdf(`/helpers/me/tax-summary.pdf?year=${year}&basis=${basis}`, { Authorization: `Bearer ${localStorage.getItem("kindred_token")}` }, `kindred-tax-summary-${year}.pdf`); }
    catch { toast.error("Couldn't prepare the tax summary."); }
    finally { setTaxBusy(false); }
  };
  useEffect(() => { api.get("/helpers/me/earnings", { params: { months: 6 } }).then((r) => setRows(r.data)); }, []);

  const csv = async () => {
    try {
      const res = await fetch(`${API}/helpers/me/earnings.csv?months=12`, { headers: { Authorization: `Bearer ${localStorage.getItem("kindred_token")}` } });
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = "kindred-earnings.csv"; a.click();
    } catch { toast.error("Couldn't download CSV."); }
  };

  if (!rows) return <div className="max-w-4xl mx-auto px-4 py-16 text-lg">Loading…</div>;
  const m = rows[sel];
  const max = Math.max(1, ...rows.map((r) => r.earned + r.reimbursed));
  const stat = (Icon, label, value, testid) => (
    <div data-testid={testid} className="bg-white border border-[#E2D9CE] rounded-2xl p-5">
      <Icon className="w-6 h-6 text-[#D96B43]" />
      <p className="text-3xl font-display font-bold text-slate-900 mt-2">{value}</p>
      <p className="text-slate-600">{label}</p>
    </div>
  );

  return (
    <div data-testid="earnings-page" className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">My earnings</h1>
          <p className="text-lg text-slate-600 mt-1">What you've earned, been reimbursed, and given as a volunteer.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <ShareImpact month={m.month} />
          <button data-testid="certificate-btn" onClick={certPdf} disabled={certBusy} className="flex items-center gap-2 bg-[#E8F4F1] hover:bg-[#d6ebe5] text-[#1F5F4F] font-semibold px-4 min-h-[48px] rounded-xl disabled:opacity-60"><Award className="w-5 h-5" /> {certBusy ? "Preparing…" : "Volunteer certificate"}</button>
          <button data-testid="earnings-csv-btn" onClick={csv} className="flex items-center gap-2 bg-white border-2 border-[#E2D9CE] hover:border-[#2E7D6B] text-slate-800 font-semibold px-4 min-h-[48px] rounded-xl"><Download className="w-5 h-5" /> Download CSV</button>
          <div className="flex items-center gap-2 bg-white border-2 border-[#E2D9CE] rounded-xl px-3 min-h-[48px]">
            <FileText className="w-5 h-5 text-[#D96B43]" />
            <select data-testid="tax-basis" value={basis} onChange={(e) => setBasis(e.target.value)} className="bg-transparent font-semibold outline-none min-h-[44px]">
              <option value="fy">Financial year (Apr–Mar)</option><option value="cy">Calendar year</option>
            </select>
            <select data-testid="tax-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-transparent font-semibold outline-none min-h-[44px]">
              {years.map((y) => <option key={y} value={y}>{basis === "fy" ? `${y}–${String(y + 1).slice(-2)}` : y}</option>)}
            </select>
            <button data-testid="tax-summary-btn" onClick={taxPdf} disabled={taxBusy} className="bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-60 text-white font-semibold px-3 min-h-[40px] rounded-lg">{taxBusy ? "Preparing…" : "Tax summary PDF"}</button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {rows.map((r, i) => (
          <button key={r.month} data-testid={`earnings-month-${r.month}`} onClick={() => setSel(i)}
            className={`px-4 min-h-[44px] rounded-xl font-semibold transition-colors ${sel === i ? "bg-[#2E7D6B] text-white" : "bg-white border border-[#E2D9CE] text-slate-700"}`}>{r.label}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stat(Wallet, "Earned from paid jobs", `₹${m.earned.toFixed(0)}`, "earnings-earned")}
        {stat(HandCoins, "Reimbursed expenses", `₹${m.reimbursed.toFixed(0)}`, "earnings-reimbursed")}
        {stat(Clock, "Hours volunteered", m.volunteer_hours.toFixed(1), "earnings-hours")}
        {stat(CheckCircle2, "Jobs completed", m.jobs, "earnings-jobs")}
      </div>

      <section className="bg-white border border-[#E2D9CE] rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-display font-bold text-slate-900 mb-4">Last 6 months (₹ earned + reimbursed)</h2>
        <div data-testid="earnings-chart" className="flex items-end gap-3 h-48">
          {[...rows].reverse().map((r, i) => {
            const total = r.earned + r.reimbursed;
            const idx = rows.length - 1 - i;
            return (
              <button key={r.month} onClick={() => setSel(idx)} className="flex-1 flex flex-col items-center gap-1 h-full justify-end" title={`${r.label}: ₹${total}`}>
                <span className="text-xs font-semibold text-slate-700">₹{total.toFixed(0)}</span>
                <span className={`w-full rounded-t-lg transition-colors ${idx === sel ? "bg-[#D96B43]" : "bg-[#2E7D6B]/70"}`} style={{ height: `${Math.max(4, (total / max) * 100)}%` }} />
                <span className="text-xs text-slate-500">{r.label.slice(0, 3)}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
